import React, { useState } from "react"
import { useSelector } from "react-redux"
import {
  Row,
  Col,
  Table,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
} from "reactstrap"

const ReviewsTable = ({ reviews = [], mode = "light" }) => {
  const [loading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(5)
  const [selectedReview, setSelectedReview] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Normalize both old and new review data structures
  const normalizedReviews = reviews.map(r => ({
    id: r.id,
    author: r.author || r.reviewBy || "Anonymous",
    reviewText: r.reviewText || r.text || "",
    rating: typeof r.rating === "string" ? parseInt(r.rating) : r.rating || 0,
    reviewDate: r.reviewDate || r.date || "",
    reviewerLocation: r.reviewerLocation || "",
    reviewerProfileUrl: r.reviewerProfileUrl || "",
    ...r,
  }))

  const filteredReviews = normalizedReviews

  // Pagination calculations
  const totalPages = Math.ceil(filteredReviews.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedReviews = filteredReviews.slice(startIndex, endIndex)

  // Generate dynamic page numbers
  const getPageNumbers = () => {
    const pages = []
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i)
        pages.push("...")
        pages.push(totalPages)
      } else if (currentPage >= totalPages - 3) {
        pages.push(1)
        pages.push("...")
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push("...")
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i)
        pages.push("...")
        pages.push(totalPages)
      }
    }
    return pages
  }
  const { organizations, isConsolidatedView } = useSelector(
    state => state.organizationReducer,
  )
  const ReviewTextExpandable = ({ text }) => {
    const [showFullReview, setShowFullReview] = useState(false)
    const maxLength = 200 // chars before truncating

    if (!text) return <p>No review text available.</p>

    const isLong = text.length > maxLength
    const displayedText = showFullReview ? text : text.slice(0, maxLength)

    return (
      <div>
        <p style={{ whiteSpace: "pre-wrap" }}>
          {displayedText}
          {isLong && !showFullReview && "..."}
        </p>

        {isLong && (
          <button
            onClick={() => setShowFullReview(!showFullReview)}
            style={{
              background: "none",
              border: "none",
              color: "#2990DF",
              cursor: "pointer",
              padding: 0,
              fontWeight: "500",
            }}
          >
            {showFullReview ? "See less" : "See more"}
          </button>
        )}
      </div>
    )
  }
  const renderStars = rating => {
    const validRating = isNaN(rating) ? 0 : Math.min(Math.max(rating, 0), 5)
    const fullStars = "★".repeat(validRating)
    const emptyStars = "☆".repeat(5 - validRating)
    return (
      <span>
        <span style={{ color: "#ffc107" }}>{fullStars}</span>
        <span style={{ color: "#dee2e6" }}>{emptyStars}</span>
      </span>
    )
  }

  const formatDate = dateString => {
    if (!dateString) return "N/A"
    const date = new Date(dateString)
    return isNaN(date.getTime())
      ? "N/A"
      : date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "2-digit",
        })
  }

  const toggleModal = () => setIsModalOpen(!isModalOpen)
  const pages = getPageNumbers()

  return (
    <>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <Row
          style={{
            borderRadius: "20px",
            backgroundColor: mode === "dark" ? "#252f40" : "#fff",
            width: "100%",
          }}
        >
          <Col xl={12}>
            <h4
              style={{
                marginBottom: "20px",
                fontSize: "16px",
                marginTop: "20px",
                marginLeft: "10px",
                color: mode === "dark" ? "#fff" : "#000",
              }}
            >
              Customer Reviews
            </h4>

            {loading ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "30vh",
                }}
              >
                <Spinner
                  style={{
                    color: mode === "dark" ? "#fff" : "#2990DF",
                    height: "3rem",
                    width: "3rem",
                  }}
                />
              </div>
            ) : filteredReviews && filteredReviews.length > 0 ? (
              <div className="table-responsive" style={{ maxHeight: "40vh" }}>
                <Table striped>
                  <thead className="sticky-header">
                    <tr>
                      <th>ID</th>
                      <th>Author</th>
                      {isConsolidatedView && <th>Organization</th>}
                      <th>Location</th>
                      <th>Review</th>
                      <th>Rating</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedReviews.map((review, index) => (
                      <tr
                        key={review.id}
                        style={{ cursor: "pointer" }}
                        onClick={() => {
                          setSelectedReview(review)
                          setIsModalOpen(true)
                        }}
                      >
                        <td>{startIndex + index + 1}</td>
                        <td>
                          {review.reviewerProfileUrl ? (
                            <a
                              href={review.reviewerProfileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: mode === "dark" ? "#2990DF" : "#0066cc",
                              }}
                            >
                              {review.author}
                            </a>
                          ) : (
                            review.author
                          )}
                        </td>
                        {isConsolidatedView && (
                          <td>
                            {organizations.find(
                              org =>
                                org.Organization.id === review.organizationId,
                            )?.Organization.name || "—"}
                          </td>
                        )}
                        <td>{review.reviewerLocation || "—"}</td>
                        <td style={{ maxWidth: "400px" }}>
                          <div
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}
                          >
                            {review.reviewText}
                          </div>
                        </td>
                        <td>{renderStars(review.rating)}</td>
                        <td>{formatDate(review.reviewDate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div
                className="text-center"
                style={{
                  height: "30vh",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                No reviews found.
              </div>
            )}
          </Col>
        </Row>

        {/* Review Details Modal */}
        <Modal isOpen={isModalOpen} toggle={toggleModal} centered>
          <ModalHeader toggle={toggleModal}>Review Details</ModalHeader>
          <ModalBody>
            {selectedReview && (
              <>
                <p>
                  <strong>Review By:</strong>{" "}
                  {selectedReview.reviewerProfileUrl ? (
                    <a
                      href={selectedReview.reviewerProfileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: mode === "dark" ? "#2990DF" : "#0066cc",
                      }}
                    >
                      {selectedReview.author}
                    </a>
                  ) : (
                    selectedReview.author
                  )}
                </p>

                {selectedReview.reviewerLocation && (
                  <p>
                    <strong>Location:</strong> {selectedReview.reviewerLocation}
                  </p>
                )}

                <p>
                  <strong>Date:</strong> {formatDate(selectedReview.reviewDate)}
                </p>

                <p>
                  <strong>Rating:</strong> {renderStars(selectedReview.rating)}
                </p>

                <hr />
                <p>
                  <strong>Review Text:</strong>
                </p>

                {/* === Review Text with "See more" toggle === */}
                <ReviewTextExpandable text={selectedReview.reviewText} />
              </>
            )}
          </ModalBody>
        </Modal>
      </div>

      {/* Pagination Controls */}
      {filteredReviews && filteredReviews.length > 0 && (
        <Row
          style={{
            marginTop: "20px",
            marginBottom: "0px",
            marginLeft: "0px",
            marginRight: "0px",
          }}
        >
          <Col xl={12} style={{ paddingLeft: "0px", paddingRight: "0px" }}>
            <div
              style={{
                boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                borderRadius: "20px",
                backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px",
                color: mode === "dark" ? "#9ca3af" : "#6b7280",
                fontSize: "14px",
              }}
            >
              {/* Records Dropdown */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span>Showing</span>
                <select
                  value={itemsPerPage}
                  onChange={e => {
                    setItemsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                  style={{
                    padding: "6px 30px 6px 12px",
                    border: `1px solid ${
                      mode === "dark" ? "#3a4556" : "#d1d5db"
                    }`,
                    borderRadius: "6px",
                    background: mode === "dark" ? "#1a2332" : "#fff",
                    color: mode === "dark" ? "#fff" : "#000",
                    fontSize: "14px",
                    cursor: "pointer",
                    outline: "none",
                    appearance: "none",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "right 8px center",
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span>of {filteredReviews.length} records</span>
              </div>

              {/* Pagination Buttons */}
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                }}
              >
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: "6px 10px",
                    border: "none",
                    background: "transparent",
                    color:
                      currentPage === 1
                        ? mode === "dark"
                          ? "#4b5563"
                          : "#d1d5db"
                        : mode === "dark"
                          ? "#9ca3af"
                          : "#6b7280",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    fontSize: "18px",
                  }}
                >
                  «
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  style={{
                    padding: "6px 10px",
                    border: "none",
                    background: "transparent",
                    color:
                      currentPage === 1
                        ? mode === "dark"
                          ? "#4b5563"
                          : "#d1d5db"
                        : mode === "dark"
                          ? "#9ca3af"
                          : "#6b7280",
                    cursor: currentPage === 1 ? "not-allowed" : "pointer",
                    fontSize: "18px",
                  }}
                >
                  ‹
                </button>

                {pages.map((page, index) =>
                  page === "..." ? (
                    <span key={`ellipsis-${index}`}>...</span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        padding: "6px 12px",
                        border: "none",
                        borderRadius: "6px",
                        background:
                          currentPage === page ? "#2990DF" : "transparent",
                        color:
                          currentPage === page
                            ? "#fff"
                            : mode === "dark"
                              ? "#9ca3af"
                              : "#6b7280",
                        cursor: "pointer",
                        minWidth: "36px",
                        fontWeight: currentPage === page ? "600" : "400",
                      }}
                    >
                      {page}
                    </button>
                  ),
                )}

                <button
                  onClick={() =>
                    setCurrentPage(prev => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  style={{
                    padding: "6px 10px",
                    border: "none",
                    background: "transparent",
                    color:
                      currentPage === totalPages
                        ? mode === "dark"
                          ? "#4b5563"
                          : "#d1d5db"
                        : mode === "dark"
                          ? "#9ca3af"
                          : "#6b7280",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                    fontSize: "18px",
                  }}
                >
                  ›
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: "6px 10px",
                    border: "none",
                    background: "transparent",
                    color:
                      currentPage === totalPages
                        ? mode === "dark"
                          ? "#4b5563"
                          : "#d1d5db"
                        : mode === "dark"
                          ? "#9ca3af"
                          : "#6b7280",
                    cursor:
                      currentPage === totalPages ? "not-allowed" : "pointer",
                    fontSize: "18px",
                  }}
                >
                  »
                </button>
              </div>
            </div>
          </Col>
        </Row>
      )}
    </>
  )
}

export default ReviewsTable
